/**
 * Utility functions for handling image URLs
 */

// Get API base URL from environment
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3000';

/**
 * Convert relative image URL to absolute URL using backend API base URL
 * 
 * @param url - The URL to convert (can be relative or absolute)
 * @returns Absolute URL that can be loaded from the backend server
 * 
 * @example
 * // Relative URL from backend
 * toAbsoluteImageUrl('/uploads/avatar_123.jpg')
 * // Returns: 'http://192.168.1.59:3000/uploads/avatar_123.jpg'
 * 
 * // Already absolute URL
 * toAbsoluteImageUrl('https://example.com/image.jpg')
 * // Returns: 'https://example.com/image.jpg'
 * 
 * // Empty or undefined
 * toAbsoluteImageUrl(null)
 * // Returns: undefined
 */
export function toAbsoluteImageUrl(url: string | null | undefined): string | undefined {
  // Return undefined for empty values
  if (!url) {
    return undefined;
  }

  // If already an absolute URL (starts with http:// or https://), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If it's a relative path (starts with /), prepend the API base URL
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`;
  }

  // If it's a relative path without leading slash, add one and prepend
  return `${API_BASE_URL}/${url}`;
}

/**
 * Ensure multiple URLs are absolute
 * Useful for batch converting URLs
 */
export function toAbsoluteImageUrls(urls: Array<string | null | undefined>): string[] {
  return urls
    .map(url => toAbsoluteImageUrl(url))
    .filter((url): url is string => url !== undefined);
}
